import { EOIcon } from '@nx/ui/eo';
import { useI18n, type TranslationKey } from '@nx/i18n';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import type { Event } from '@nx/domain';

function statusKind(status: Event['status']): string {
  if (status === 'published') return 'olive';
  if (status === 'archived') return 'muted';
  return 'clay';
}

function statusLabel(status: Event['status'], t: (k: TranslationKey) => string): string {
  if (status === 'published') return t('eo.event.status.live');
  if (status === 'archived') return t('eo.event.status.archived');
  return t('eo.event.status.draft');
}

export function EventsListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const events = useAppSelector((s) => s.events.items);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>{t('eo.nav.events')}</h2>
          <p>{t('eo.events.subtitle')}</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn btn-outline">
            <EOIcon n="filter" size={16} />
            {t('eo.common.filter')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/eo/events/new')}>
            <EOIcon n="plus" size={16} />
            {t('eo.events.new')}
          </button>
        </div>
      </div>

      <div className="events-grid">
        {events.map((ev) => (
          <div
            key={ev.id}
            className="event-card"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/eo/events/${ev.id}/agenda`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/eo/events/${ev.id}/agenda`)}
          >
            <div
              className="cover"
              style={{
                background: ev.coverImageUrl
                  ? `url(${ev.coverImageUrl}) center/cover`
                  : 'linear-gradient(125deg, #0A1628, #1B2E4A)',
              }}
            >
              <div className="corner">
                <span className={`badge ${statusKind(ev.status)}`}>
                  {statusLabel(ev.status, t)}
                </span>
              </div>
              <div className="dates">
                {ev.startDate.toISOString().slice(0, 10)} — {ev.endDate.toISOString().slice(0, 10)}
              </div>
            </div>
            <div className="body">
              <h3>{ev.title}</h3>
              <div className="meta">
                <EOIcon n="pin" size={14} />
                {ev.location ?? '—'}
              </div>
              {ev.maxParticipants != null ? (
                <div className="meta" style={{ marginTop: 4 }}>
                  <EOIcon n="users" size={14} />
                  {t('eo.events.cap')} {ev.maxParticipants}
                </div>
              ) : null}
            </div>
          </div>
        ))}
        <button type="button" className="event-card new" onClick={() => navigate('/eo/events/new')}>
          <span className="plus">
            <EOIcon n="plus" size={22} />
          </span>
          <strong>{t('eo.events.new')}</strong>
          <small>{t('eo.events.newHint')}</small>
        </button>
      </div>
    </>
  );
}
